// The item drawer (?item=<id>, or ?new=<categoryId> for a new item): the fields every item needs on the first screen
// (EDT-03), "Altri dettagli" behind a disclosure, and a footer with ‹ › over the list it was opened from (EDT-04) and a
// ⋮ with Duplica and Elimina. Each field saves when it loses focus, with no edit mode and no Save button, and a save
// that fails says why under its field. Elimina deletes at once and the page's toast offers Annulla: DD-051's scoped
// exception for the menu drawer. A session that only reads sees every field disabled and no ⋮.
import { useRef, useState } from "react";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, DotsVerticalIcon } from "@/components/icons";
import { Drawer } from "@/components/ui/Drawer";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import { cn } from "@/utils/cn";
import { saveError } from "../format";
import type { MenuMutations } from "../hooks/useMenu";
import type { ItemPatch, MenuCategory, MenuDetail, MenuItem, PricePoint } from "../types";
import { AllergenEditor } from "./AllergenEditor";
import { BlurText, Chip, Field, FieldError, NameStep, Section } from "./drawerFields";
import { HiddenBadge } from "./ItemRow";
import { OtherDetails } from "./OtherDetails";
import { PricePointsEditor } from "./PricePointsEditor";
import { ToggleSwitch } from "./ToggleSwitch";

/** The fields that save on their own, each with its own error line. */
type FieldKey =
  | "name" | "description" | "highlight" | "prices" | "priceHidden" | "allergens" | "labelIds" | "hidden"
  | "ingredients" | "photoUrl" | "cold" | "wine";

interface Props {
  menu: MenuDetail | undefined;
  mutations: MenuMutations;
  itemId: string | null;
  /** The category a new item goes in, while the drawer asks for its name. */
  newIn: MenuCategory | null;
  /** A wine category's new item starts with Calice and Bottiglia. */
  newIsWine: boolean;
  /** The ordered ids the item was opened from (a category, the search results), which ‹ › walk. */
  list: string[];
  onNavigate: (itemId: string) => void;
  /** The new item exists: the drawer goes on as its drawer. */
  onCreated: (itemId: string) => void;
  onClose: () => void;
  /** Shows the page's toast with Annulla, so a Duplica or an Elimina reads like any other. */
  onUndoable: (message: string, undo: () => void) => void;
  onFailed: (message: string) => void;
}

/** A wine: a drink with wine details, or in a category whose drinks carry them. */
function isWine(item: MenuItem, menu: MenuDetail): boolean {
  if (!item.isDrink) return false;
  return item.wine !== null || menu.items.some((i) => i.categoryId === item.categoryId && i.wine !== null);
}

function ItemBody({ item, menu, disabled, onSave, errors }: {
  item: MenuItem;
  menu: MenuDetail;
  disabled: boolean;
  onSave: (field: FieldKey, patch: ItemPatch) => void;
  errors: Partial<Record<FieldKey, string>>;
}) {
  const [more, setMore] = useState(false);
  const toggleLabel = (id: string) =>
    onSave("labelIds", { labelIds: item.labelIds.includes(id) ? item.labelIds.filter((l) => l !== id) : [...item.labelIds, id] });
  const filled = [
    item.ingredients.length > 0 && `${item.ingredients.length} ingredienti`,
    item.photoUrl && "foto",
    item.cold && "freddo",
    item.wine?.producer && "produttore",
  ].filter(Boolean).join(" · ");

  return (
    // A fieldset disables every control at once for a session that only reads; "Altri dettagli" stays outside it, so
    // the details can still be read.
    <div className="space-y-7">
    <fieldset disabled={disabled} className="min-w-0 space-y-7">
      <div className="space-y-3">
        {/* The name heads the body, not the fixed header, so a long one shows whole and scrolls away with the form. */}
        <div>
          <BlurText
            value={item.name}
            onCommit={(name) => onSave("name", { name })}
            required
            wrap
            disabled={disabled}
            ariaLabel="Nome"
            placeholder="Nome"
            className="-mt-4 rounded-none border-0 border-b border-transparent px-0 py-1 text-[18px] font-semibold leading-[25px] hover:border-gray-200 focus:border-indigo-300 focus:ring-0"
          />
          <FieldError message={errors.name} />
        </div>
        <Field label="Descrizione">
          <BlurText
            value={item.description}
            onCommit={(description) => onSave("description", { description })}
            multiline
            rows={2}
            disabled={disabled}
            placeholder="Come lo descriveresti a un cliente"
          />
        </Field>
        <FieldError message={errors.description} />
        <Field label="Riga in evidenza">
          <BlurText
            value={item.highlight}
            onCommit={(highlight) => onSave("highlight", { highlight })}
            disabled={disabled}
            placeholder="Es. Piatto della settimana"
          />
        </Field>
        <FieldError message={errors.highlight} />
      </div>

      <Section title="Prezzi">
        <PricePointsEditor
          prices={item.prices}
          priceHidden={item.priceHidden}
          isWine={isWine(item, menu)}
          disabled={disabled}
          error={errors.prices ?? errors.priceHidden}
          onSave={(prices: PricePoint[]) => onSave("prices", { prices })}
          onPriceHidden={(priceHidden) => onSave("priceHidden", { priceHidden })}
        />
      </Section>

      <Section title="Allergeni">
        <AllergenEditor item={item} disabled={disabled} error={errors.allergens} onSave={(patch) => onSave("allergens", patch)} />
      </Section>

      {menu.labels.length > 0 && (
        <Section title="Etichette">
          <div className="flex flex-wrap gap-1.5">
            {menu.labels.map((label) => (
              <Chip key={label.id} on={item.labelIds.includes(label.id)} disabled={disabled} onClick={() => toggleLabel(label.id)}>
                {label.name}
              </Chip>
            ))}
          </div>
          <FieldError message={errors.labelIds} />
        </Section>
      )}

      <div>
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-gray-900">Nascosto</p>
            <p className="text-[12px] leading-4 text-gray-500">Non compare nel menu dei clienti.</p>
          </div>
          <ToggleSwitch on={item.hidden} label="Nascosto" disabled={disabled} onChange={(hidden) => onSave("hidden", { hidden })} />
        </div>
        <FieldError message={errors.hidden} />
      </div>
    </fieldset>

      <div className="border-t border-gray-100 pt-2">
        <button
          type="button"
          aria-expanded={more}
          aria-label="Altri dettagli"
          onClick={() => setMore(!more)}
          className="flex min-h-[44px] w-full items-center gap-2 text-left"
        >
          <span className="text-[13px] font-semibold text-gray-900">Altri dettagli</span>
          {!more && filled && <span className="min-w-0 truncate text-[12px] text-gray-500">{filled}</span>}
          <ChevronDownIcon className={cn("ml-auto h-4 w-4 shrink-0 text-gray-400 transition-transform", !more && "-rotate-90")} />
        </button>
        {more && (
          <fieldset disabled={disabled} className="min-w-0 pt-2">
            <OtherDetails item={item} disabled={disabled} errors={errors} onSave={onSave} />
          </fieldset>
        )}
      </div>
    </div>
  );
}

export function ItemDrawer({
  menu, mutations, itemId, newIn, newIsWine, list, onNavigate, onCreated, onClose, onUndoable, onFailed,
}: Props) {
  const [errors, setErrors] = useState<{ itemId: string | null; byField: Partial<Record<FieldKey, string>> }>({ itemId: null, byField: {} });
  const [createError, setCreateError] = useState<string | null>(null);
  const canEdit = menu?.canEdit ?? false;

  const item = itemId ? menu?.items.find((i) => i.id === itemId) : undefined;
  // The drawer keeps showing the last item while it slides out.
  const last = useRef<MenuItem | undefined>(undefined);
  if (item) last.current = item;
  const shown = item ?? (newIn ? undefined : last.current);
  const current = useRef(itemId);
  current.current = itemId;

  const live = new Set(menu?.items.map((i) => i.id) ?? []);
  const walk = list.filter((id) => live.has(id));
  const index = itemId ? walk.indexOf(itemId) : -1;
  const prev = index > 0 ? walk[index - 1] : null;
  const next = index >= 0 && index < walk.length - 1 ? walk[index + 1] : null;
  const category = shown ? menu?.categories.find((c) => c.id === shown.categoryId) : newIn ?? undefined;
  const fieldErrors = errors.itemId === shown?.id ? errors.byField : {};

  const setFieldError = (target: string, field: FieldKey, message: string | null) =>
    setErrors((now) => {
      const byField = now.itemId === target ? { ...now.byField } : {};
      if (message === null) delete byField[field];
      else byField[field] = message;
      return { itemId: target, byField };
    });

  const save = (target: string) => (field: FieldKey, patch: ItemPatch) =>
    mutations.patchItem.mutate({ itemId: target, patch }, {
      onSuccess: () => setFieldError(target, field, null),
      onError: (error) => setFieldError(target, field, saveError(error)),
    });

  const create = (name: string) => {
    if (!newIn) return;
    setCreateError(null);
    const wine: PricePoint[] = [
      { label: "Calice", amountMinor: null, unit: "€" },
      { label: "Bottiglia", amountMinor: null, unit: "€" },
    ];
    mutations.createItem.mutate({ categoryId: newIn.id, name, ...(newIsWine ? { prices: wine } : {}) }, {
      onSuccess: (answer) => onCreated(answer.item.id),
      onError: (error) => setCreateError(saveError(error)),
    });
  };

  const duplicate = (original: MenuItem) =>
    mutations.duplicateItem.mutate(original.id, {
      onSuccess: (answer) => {
        onNavigate(answer.item.id);
        onUndoable(`Duplicato: ${original.name}`, () =>
          mutations.deleteItem.mutate(answer.item.id, {
            onSuccess: () => {
              if (current.current === answer.item.id) onNavigate(original.id);
            },
            onError: (error) => onFailed(saveError(error)),
          }));
      },
      onError: (error) => onFailed(saveError(error)),
    });
  const remove = (gone: MenuItem) => {
    onClose();
    mutations.deleteItem.mutate(gone.id, {
      onSuccess: () => onUndoable(`Eliminato: ${gone.name}`, () =>
        mutations.restoreItem.mutate(gone.id, { onError: (error) => onFailed(saveError(error)) })),
      onError: (error) => onFailed(saveError(error)),
    });
  };

  const header = (
    <p className="flex min-h-[24px] items-center gap-1.5 text-[13px] font-medium text-gray-500">
      <span className="truncate">{newIn && !shown ? `Nuova voce in ${newIn.name}` : category?.name ?? "Voce"}</span>
      {shown?.hidden && <HiddenBadge />}
    </p>
  );

  const arrow = "grid h-[40px] w-[40px] place-items-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent";
  const footer = shown && (
    <>
      <div className="mr-auto flex items-center">
        <button type="button" className={arrow} disabled={!prev} onClick={() => prev && onNavigate(prev)} aria-label="Voce precedente">
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        {index >= 0 && (
          <span className="min-w-[56px] text-center text-[12px] tabular-nums text-gray-500">
            {index + 1} di {walk.length}
          </span>
        )}
        <button type="button" className={arrow} disabled={!next} onClick={() => next && onNavigate(next)} aria-label="Voce successiva">
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>
      {canEdit && (
        <DropdownMenu
          items={[
            { id: "duplicate", label: "Duplica", onClick: () => duplicate(shown) },
            { id: "delete", label: "Elimina", variant: "danger", onClick: () => remove(shown) },
          ]}
          width="w-44"
          align="right"
          menuClassName="rounded-xl"
          trigger={
            <button
              type="button"
              aria-label={`Azioni: ${shown.name}`}
              className="grid h-[40px] w-[40px] place-items-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              <DotsVerticalIcon className="h-5 w-5" />
            </button>
          }
        />
      )}
    </>
  );

  return (
    <Drawer
      isOpen={(itemId !== null && item !== undefined) || newIn !== null}
      onClose={onClose}
      header={header}
      footer={footer}
      bodyPadding
      title={shown?.name ?? (newIn ? "Nuova voce" : "Voce")}
      a11yDescription="I dettagli della voce del menu. Ogni campo si salva quando lo lasci."
      className="md:max-w-lg"
      // An item opens on the item, not on the name's input: a phone would raise its keyboard over the form. A new
      // one's name input takes the focus itself (autoFocus), which Radix then leaves where it is.
      onOpenAutoFocus={(event) => event.preventDefault()}
    >
      {newIn && !shown ? (
        <NameStep
          key={`new-${newIn.id}`}
          busy={mutations.createItem.isPending}
          error={createError}
          hint="Basta il nome: prezzi, allergeni e il resto si aggiungono dopo."
          onName={create}
        />
      ) : shown && menu ? (
        <ItemBody key={shown.id} item={shown} menu={menu} disabled={!canEdit} errors={fieldErrors} onSave={save(shown.id)} />
      ) : null}
    </Drawer>
  );
}
